import { GetMigratedProperties } from "../../features/project_migration/InteractionMigrationHelper";
import { openAnswersProperty, hasOpenAnswers } from "../OpenAnswers";
import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import { LocalizedStringRecord } from "../../app/slices/TrainingLocalizationSlice";
import { InteractionFactoryEntity } from "../../features/interaction_factory/InteractionFactory";
import ImageButtonQuizPreview from "../../features/previews/image_button_quiz/ImageButtonQuizPreview";
import { ValidationHelper } from "../../features/validation/ValidationHelper";
import { GetNewGuid } from "../../helpers/GuidHelper";
import { Actor } from "../Actor";
import { Prop } from "../Prop";
import { IFormQuiz } from "../Quiz";
import Take from "../Take";
import Interaction, { InteractionType } from "./Interaction";

export const ImageButtonQuizInteraction = (): InteractionFactoryEntity => {
  const view = <ImageButtonQuizPreview />;

  const create = (getKeys: (amount: number) => string[]): IFormQuiz => {
    const keys = getKeys(1);

    return {
      id: GetNewGuid(),
      name: "Image Button Quiz",
      type: InteractionType.ImageButtonQuiz,
      title: keys[0],
      answers: [
        {
          id: GetNewGuid(),
          text: "",
          assetId: "",
          correct: false,
        },
        {
          id: GetNewGuid(),
          text: "",
          assetId: "",
          correct: false,
        },
      ],
      properties: [{ ...openAnswersProperty }],
    };
  };

  const migrate = (
    from: number,
    to: number,
    interaction: Interaction
  ): IFormQuiz => {
    return {
      ...interaction,
      properties: GetMigratedProperties([openAnswersProperty], interaction.properties ?? []),
    } as IFormQuiz;
  };

  const copy = (
    interaction: Interaction,
    getKeys: (amount: number) => string[],
    replaceKey: (from: string, to: string) => void
  ): IFormQuiz => {
    const original = interaction as IFormQuiz;
    const [title] = getKeys(1);
    replaceKey(original.title, title);

    return {
      ...original,
      id: GetNewGuid(),
      title,
      answers: original.answers.map((answer) => ({
        ...answer,
        id: GetNewGuid(),
      })),
      properties: original.properties?.map((property) => ({ ...property })),
    };
  };

  const validate = (
    interaction: Interaction,
    getValue: (key: string) => string,
    take: Take,
    actorPresets: Actor[]
  ): string[] => {
    const converted = interaction as IFormQuiz;
    const result: string[] = [];

    // validate title
    ValidationHelper.ValidateKey(converted.title, getValue, result, "Title");

    return result;
  };

  const filter = (
    interaction: Interaction,
    takeName: string,
    getMatchingResults: (
      searchableStrings: string[],
      takeName: string,
      interaction: Interaction,
      filterType: string
    ) => SearchResultData[]
  ): SearchResultData[] => {
    const converted = interaction as IFormQuiz;
    const searchableStrings: string[] = [interaction.name, converted.title];

    return getMatchingResults(
      searchableStrings,
      takeName,
      interaction,
      filterType
    );
  };

  const filterType: string = "Image Button Quiz";

  const validateErrors = (
    interaction: Interaction,
    parentTake: Take,
    currentTake: Take,
    actorPresets: Actor[],
    allPropPresets: Prop[],
    localizedStringRecords: LocalizedStringRecord
  ): string[] => {
    const casted = interaction as IFormQuiz;
    const errors: string[] = [];

    if (casted.answers.length === 0) errors.push("Please add an answer");

    if (!hasOpenAnswers(casted) && !casted.answers.some((a) => a.correct)) {
      errors.push("Quiz must have one correct answer");
    }

    for (let i = 0; i < casted.answers.length; i++) {
      ValidationHelper.ValidateAssetId(
        casted.answers[i].assetId ?? "",
        errors,
        `Image ${i + 1}`
      );
    }

    return errors;
  };

  return {
    View: view,
    FilterType: filterType,
    Create: create,
    Migrate: migrate,
    Copy: copy,
    Validate: validate,
    Filter: filter,
    ValidateErrors: validateErrors,
  };
};
